import {
  AlertCircle,
  Calendar,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Clock,
  Eye,
  Filter,
  Mail,
  Phone,
  Plus,
  Search,
  User,
  X,
  XCircle,
} from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { tagAPI } from "../../services/api";
import employeeService from "../../services/employee";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { useToast } from "../ui/use-toast";

interface TagItem {
  id: string;
  name: string;
  color: string;
}

interface Employee {
  employeeId: string;
  fullName: string;
  email?: string;
  phoneE164?: string;
  status: string;
  createdAt: string;
  orgId: string;
  candidateId?: string;
  badgeUrl?: string;
  source: string;
  isUKNative?: boolean;
  ukNativeType?: string;
  tags?: TagItem[];
  verificationSteps?: {
    email?: { status: string; completedAt?: string };
    phone?: { status: string; completedAt?: string };
    id?: { status: string; completedAt?: string };
    rtw?: { status: string; completedAt?: string };
  };
}

interface Pagination {
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

interface EmployeeDetail extends Employee {
  inviteChannelPref?: string;
  expiresAt?: string;
  lastActivityAt?: string;
  verificationToken?: string;
}

const PAGE_SIZE_OPTIONS = [10, 20, 50];

const EmployeesPage: React.FC = () => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedEmployee, setSelectedEmployee] =
    useState<EmployeeDetail | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Tag filtering
  const [availableTags, setAvailableTags] = useState<TagItem[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [tagFilterOpen, setTagFilterOpen] = useState(false);

  const { toast } = useToast();
  const navigate = useNavigate();

  const loadTags = async () => {
    try {
      const response = await tagAPI.listTags();
      setAvailableTags(response.data);
    } catch (error) {
      console.error("Failed to load tags:", error);
    }
  };

  const loadEmployees = useCallback(
    async (page: number = currentPage, size: number = pageSize) => {
      try {
        setLoading(true);
        const data = await employeeService.getEmployeeList(
          undefined,
          searchQuery || undefined,
          size,
          undefined,
          selectedTagIds.length > 0 ? selectedTagIds : undefined,
          page,
          size
        );
        setEmployees(data.items || []);
        setPagination(data.pagination || null);
      } catch (error: any) {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [toast, searchQuery, selectedTagIds, currentPage, pageSize]
  );

  useEffect(() => {
    loadTags();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
    loadEmployees(1, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, selectedTagIds, pageSize]);

  useEffect(() => {
    loadEmployees(currentPage, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const toggleTagFilter = (tagId: string) => {
    setSelectedTagIds((prev) => {
      if (prev.includes(tagId)) {
        return prev.filter((id) => id !== tagId);
      }
      return [...prev, tagId];
    });
  };

  const clearTagFilters = () => {
    setSelectedTagIds([]);
  };

  const handleEmployeeClick = async (employee: Employee) => {
    try {
      const detail = await employeeService.getEmployeeDetail(
        employee.employeeId
      );

      // Normalize backend detail to component shape
      const stepsArray = detail.flowStatus?.steps || [];
      const verificationsArray = detail.verifications || [];
      const stepsObj: any = {
        email: { status: "pending" },
        phone: { status: "pending" },
        id: { status: "pending" },
        rtw: { status: "pending" },
      };
      stepsArray.forEach((s: any) => {
        if (stepsObj[s.type] !== undefined) {
          // Find corresponding verification to get createdAt
          const verification = verificationsArray.find(
            (v: any) => v.type === s.type
          );
          stepsObj[s.type] = {
            status: s.status,
            completedAt: verification?.createdAt,
          };
        }
      });

      const normalized: EmployeeDetail = {
        employeeId: detail.employee?.employeeId || employee.employeeId,
        orgId: detail.employee?.orgId || (employee as any).orgId,
        fullName: detail.employee?.fullName || employee.fullName,
        email: detail.employee?.email || employee.email,
        phoneE164: detail.employee?.phoneE164 || employee.phoneE164,
        status: detail.employee?.status || employee.status,
        candidateId: detail.employee?.candidateId,
        badgeUrl: detail.employee?.badgeUrl,
        source: detail.employee?.source || (employee as any).source,
        createdAt: detail.employee?.createdAt || employee.createdAt,
        inviteChannelPref: detail.employee?.inviteChannelPref,
        expiresAt: detail.employee?.expiresAt,
        lastActivityAt: detail.employee?.lastActivityAt,
        verificationToken: detail.employee?.verificationToken,
        verificationSteps: stepsObj,
        isUKNative: detail.employee?.isUKNative,
        ukNativeType: detail.employee?.ukNativeType,
      } as any;

      setSelectedEmployee(normalized);
      setIsDialogOpen(true);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const allStepsPassed = (e: Employee) => {
    const steps = e.verificationSteps;
    if (!steps) return false;
    const emailPassed = steps.email?.status === "pass";
    const phonePassed = steps.phone?.status === "pass";
    const idPassed = steps.id?.status === "pass";
    const rtwPassed =
      steps.rtw?.status === "pass" || steps.rtw?.status === "skipped";
    return emailPassed && phonePassed && idPassed && rtwPassed;
  };

  const getStatusBadge = (employee: Employee) => {
    // If all steps are passed/skipped, show as verified
    const isVerified = allStepsPassed(employee);
    const displayStatus = isVerified ? "verified" : employee.status;

    const statusConfig = {
      invited: {
        variant: "secondary" as const,
        icon: Clock,
        label: "Invited",
        className: "",
      },
      verifying: {
        variant: "default" as const,
        icon: AlertCircle,
        label: "Verifying",
        className: "",
      },
      verified: {
        variant: "default" as const,
        icon: CheckCircle,
        label: "Verified",
        className: "bg-green-500 hover:bg-green-600 text-white border-green-600",
      },
      completed: {
        variant: "default" as const,
        icon: CheckCircle,
        label: "Completed",
        className: "bg-green-500 hover:bg-green-600 text-white border-green-600",
      },
      failed: {
        variant: "destructive" as const,
        icon: XCircle,
        label: "Failed",
        className: "",
      },
    };

    const config =
      statusConfig[displayStatus as keyof typeof statusConfig] ||
      statusConfig.invited;
    const IconComponent = config.icon;

    return (
      <Badge
        variant={config.variant}
        className={`flex items-center gap-1 ${config.className}`}
      >
        <IconComponent className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const getStepStatus = (stepStatus?: string) => {
    if (!stepStatus) {
      return (
        <div className="flex items-center gap-1">
          <Clock className="h-3 w-3 text-gray-400" />
          <span className="text-sm text-gray-400">Pending</span>
        </div>
      );
    }

    const statusConfig = {
      pass: { icon: CheckCircle, color: "text-green-600", label: "Passed" },
      fail: { icon: XCircle, color: "text-red-600", label: "Failed" },
      pending: { icon: Clock, color: "text-yellow-600", label: "Pending" },
      skipped: { icon: CheckCircle, color: "text-green-600", label: "Skipped" },
    };

    const config =
      statusConfig[stepStatus as keyof typeof statusConfig] ||
      statusConfig.pending;
    const IconComponent = config.icon;

    return (
      <div className="flex items-center gap-1">
        <IconComponent className={`h-3 w-3 ${config.color}`} />
        <span className={`text-sm ${config.color}`}>{config.label}</span>
      </div>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading employees...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Employees</h1>
          <p className="text-muted-foreground">
            Manage and track your employee verification processes
          </p>
        </div>
        <Button onClick={() => navigate("/widget")}>
          <Plus className="mr-2 h-4 w-4" />
          Add Employee
        </Button>
      </div>

      {/* Search, Filter and Stats */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search employees..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Tag Filter */}
        {availableTags.length > 0 && (
          <Popover open={tagFilterOpen} onOpenChange={setTagFilterOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className={selectedTagIds.length > 0 ? "border-primary" : ""}
              >
                <Filter className="h-4 w-4 mr-2" />
                {selectedTagIds.length > 0
                  ? `${selectedTagIds.length} tag${selectedTagIds.length > 1 ? "s" : ""}`
                  : "Filter by Tags"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-2" align="start">
              <div className="space-y-1">
                {availableTags.map((tag) => {
                  const isSelected = selectedTagIds.includes(tag.id);
                  return (
                    <button
                      key={tag.id}
                      type="button"
                      onClick={() => toggleTagFilter(tag.id)}
                      className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm hover:bg-muted transition-colors ${
                        isSelected ? "bg-muted" : ""
                      }`}
                    >
                      <span
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: tag.color }}
                      />
                      <span className="flex-1 text-left">{tag.name}</span>
                      {isSelected && (
                        <CheckCircle className="h-4 w-4 text-primary" />
                      )}
                    </button>
                  );
                })}
                {selectedTagIds.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full mt-2"
                    onClick={clearTagFilters}
                  >
                    <X className="h-4 w-4 mr-2" />
                    Clear Filters
                  </Button>
                )}
              </div>
            </PopoverContent>
          </Popover>
        )}

        {/* Selected Tags Display */}
        {selectedTagIds.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {selectedTagIds.map((tagId) => {
              const tag = availableTags.find((t) => t.id === tagId);
              if (!tag) return null;
              return (
                <Badge
                  key={tagId}
                  variant="secondary"
                  className="flex items-center gap-1 pr-1"
                  style={{
                    backgroundColor: `${tag.color}20`,
                    borderColor: tag.color,
                    color: tag.color,
                  }}
                >
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: tag.color }}
                  />
                  {tag.name}
                  <button
                    type="button"
                    onClick={() => toggleTagFilter(tagId)}
                    className="ml-1 hover:bg-black/10 rounded p-0.5"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              );
            })}
          </div>
        )}

        <div className="text-sm text-muted-foreground ml-auto">
          {pagination
            ? `${pagination.totalCount} employee${pagination.totalCount !== 1 ? "s" : ""}`
            : `${employees.length} employees`}
        </div>
      </div>

      {/* Employees Table */}
      <Card>
        <CardHeader>
          <CardTitle>Employee List</CardTitle>
        </CardHeader>
        <CardContent>
          {employees.length === 0 ? (
            <div className="text-center py-8">
              <User className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No employees found</h3>
              <p className="text-muted-foreground mb-4">
                {searchQuery || selectedTagIds.length > 0
                  ? "Try adjusting your search or filter criteria."
                  : "Start by adding your first employee."}
              </p>
              {!searchQuery && selectedTagIds.length === 0 && (
                <Button onClick={() => navigate("/widget")}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Your First Employee
                </Button>
              )}
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Tags</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Verification Steps</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {employees.map((employee) => (
                    <TableRow
                      key={employee.employeeId}
                      className="cursor-pointer hover:bg-muted/50"
                    >
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                            <User className="h-4 w-4 text-primary" />
                          </div>
                          <div>
                            <div className="font-medium flex items-center gap-2">
                              {employee.fullName}
                              {allStepsPassed(employee) && (
                                <Badge variant="secondary" className="text-xxs">
                                  Verified
                                </Badge>
                              )}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              ID: {employee.employeeId.slice(0, 8)}...
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          {employee.email && (
                            <div className="flex items-center gap-1 text-sm">
                              <Mail className="h-3 w-3 text-muted-foreground" />
                              {employee.email}
                            </div>
                          )}
                          {employee.phoneE164 && (
                            <div className="flex items-center gap-1 text-sm">
                              <Phone className="h-3 w-3 text-muted-foreground" />
                              {employee.phoneE164}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {employee.tags && employee.tags.length > 0 ? (
                            employee.tags.map((tag) => (
                              <Badge
                                key={tag.id}
                                variant="secondary"
                                className="text-xs"
                                style={{
                                  backgroundColor: `${tag.color}20`,
                                  borderColor: tag.color,
                                  color: tag.color,
                                }}
                              >
                                {tag.name}
                              </Badge>
                            ))
                          ) : (
                            <span className="text-sm text-muted-foreground">—</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{getStatusBadge(employee)}</TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <div className="text-xs">
                            Email:{" "}
                            {getStepStatus(
                              employee.verificationSteps?.email?.status
                            )}
                          </div>
                          <div className="text-xs">
                            Phone:{" "}
                            {getStepStatus(
                              employee.verificationSteps?.phone?.status
                            )}
                          </div>
                          <div className="text-xs">
                            ID:{" "}
                            {getStepStatus(
                              employee.verificationSteps?.id?.status
                            )}
                          </div>
                          <div className="text-xs">
                            RTW:{" "}
                            {getStepStatus(
                              employee.verificationSteps?.rtw?.status
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-sm">
                          <Calendar className="h-3 w-3 text-muted-foreground" />
                          {formatDate(employee.createdAt)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEmployeeClick(employee)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {/* Pagination Controls */}
              {pagination && pagination.totalPages > 0 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Rows per page:</span>
                    <Select
                      value={pageSize.toString()}
                      onValueChange={(value) => setPageSize(parseInt(value, 10))}
                    >
                      <SelectTrigger className="w-[80px] h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PAGE_SIZE_OPTIONS.map((size) => (
                          <SelectItem key={size} value={size.toString()}>
                            {size}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center gap-4">
                    <span className="text-sm text-muted-foreground">
                      Page {pagination.page} of {pagination.totalPages}
                    </span>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                        disabled={!pagination.hasPrevPage}
                      >
                        <ChevronLeft className="h-4 w-4" />
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setCurrentPage((prev) =>
                            Math.min(pagination.totalPages, prev + 1)
                          )
                        }
                        disabled={!pagination.hasNextPage}
                      >
                        Next
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Employee Detail Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Employee Details</DialogTitle>
            <DialogDescription>
              Complete information about this employee's verification process
            </DialogDescription>
          </DialogHeader>

          {selectedEmployee && (
            <div className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Full Name
                  </label>
                  <p className="text-lg font-semibold">
                    {selectedEmployee.fullName}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Status
                  </label>
                  <div className="mt-1">
                    {getStatusBadge(selectedEmployee)}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Email
                  </label>
                  <p className="text-sm">
                    {selectedEmployee.email || "Not provided"}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Phone
                  </label>
                  <p className="text-sm">
                    {selectedEmployee.phoneE164 || "Not provided"}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Invite Channel
                  </label>
                  <p className="text-sm capitalize">
                    {selectedEmployee.inviteChannelPref || "Not specified"}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Employee ID
                  </label>
                  <p className="text-sm font-mono">
                    {selectedEmployee.employeeId}
                  </p>
                </div>
              </div>

              {/* UK Native Status */}
              {selectedEmployee.ukNativeType && (
                <div className="mb-4 p-4 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-800">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                    <div>
                      <p className="text-sm font-medium text-green-900 dark:text-green-100">
                        UK Status: {
                          selectedEmployee.ukNativeType === "uk_ireland" ? "UK/Ireland national" :
                          selectedEmployee.ukNativeType === "eea_settlement" ? "EEA national with eVisas or EU Settlement Scheme" :
                          "Others with physical BRP/visa vignette"
                        }
                      </p>
                      <p className="text-xs text-green-700 dark:text-green-300">
                        This employee has UK status. UK Work Permit verification
                        was skipped. Verification completed with 3 steps (Email,
                        Phone, ID).
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Verification Steps */}
              <div>
                <h3 className="text-lg font-semibold mb-3">
                  Verification Steps
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <div className="font-medium">Email Verification</div>
                        <div className="text-sm text-muted-foreground">
                          {selectedEmployee.verificationSteps?.email?.status ===
                          "pass"
                            ? selectedEmployee.verificationSteps.email
                                .completedAt
                              ? `Completed: ${formatDate(
                                  selectedEmployee.verificationSteps.email
                                    .completedAt
                                )}`
                              : "Completed"
                            : "Not completed"}
                        </div>
                      </div>
                      {getStepStatus(
                        selectedEmployee.verificationSteps?.email?.status
                      )}
                    </div>
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <div className="font-medium">Phone Verification</div>
                        <div className="text-sm text-muted-foreground">
                          {selectedEmployee.verificationSteps?.phone?.status ===
                          "pass"
                            ? selectedEmployee.verificationSteps.phone
                                .completedAt
                              ? `Completed: ${formatDate(
                                  selectedEmployee.verificationSteps.phone
                                    .completedAt
                                )}`
                              : "Completed"
                            : "Not completed"}
                        </div>
                      </div>
                      {getStepStatus(
                        selectedEmployee.verificationSteps?.phone?.status
                      )}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <div className="font-medium">ID Verification</div>
                        <div className="text-sm text-muted-foreground">
                          {selectedEmployee.verificationSteps?.id?.status ===
                          "pass"
                            ? selectedEmployee.verificationSteps.id.completedAt
                              ? `Completed: ${formatDate(
                                  selectedEmployee.verificationSteps.id
                                    .completedAt
                                )}`
                              : "Completed"
                            : "Not completed"}
                        </div>
                      </div>
                      {getStepStatus(
                        selectedEmployee.verificationSteps?.id?.status
                      )}
                    </div>
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <div className="font-medium">Right to Work</div>
                        <div className="text-sm text-muted-foreground">
                          {selectedEmployee.verificationSteps?.rtw?.status ===
                          "skipped"
                            ? "Skipped (UK Native)"
                            : selectedEmployee.verificationSteps?.rtw?.status ===
                              "pass"
                            ? selectedEmployee.verificationSteps.rtw.completedAt
                              ? `Completed: ${formatDate(
                                  selectedEmployee.verificationSteps.rtw
                                    .completedAt
                                )}`
                              : "Completed"
                            : "Not completed"}
                        </div>
                      </div>
                      {getStepStatus(
                        selectedEmployee.verificationSteps?.rtw?.status
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Additional Info */}
              <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Created At
                  </label>
                  <p className="text-sm">
                    {formatDate(selectedEmployee.createdAt)}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Expires At
                  </label>
                  <p className="text-sm">
                    {selectedEmployee.expiresAt
                      ? formatDate(selectedEmployee.expiresAt)
                      : "Not set"}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Verification Token
                  </label>
                  <p className="text-sm font-mono break-all">
                    {selectedEmployee.verificationToken || "Not available"}
                  </p>
                </div>
                {selectedEmployee.lastActivityAt && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Last Activity
                    </label>
                    <p className="text-sm">
                      {formatDate(selectedEmployee.lastActivityAt)}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmployeesPage;
